import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import { Intersection } from "@splidejs/splide-extension-intersection";
import "@splidejs/splide/dist/css/splide.min.css";
import { useEffect, useRef } from "react";
import styles from "./Section3.module.scss";

const carousel1 = [
  {
    title: "Albany Public Library",
    image: "albany_public_library_color.png",
  },
  {
    title: "Anatomy IT",
    image: "anatomy_it_color.png",
  },
  {
    title: "CrowdStrike",
    image: "crowdstrike_color.png",
  },
  {
    title: "CyberSafe Solutions",
    image: "cybersafe_solutions_color.png",
  },
  {
    title: "Databricks",
    image: "databricks_color.png",
  },
  {
    title: "Datadog",
    image: "datadog_1_color.png",
  },
  {
    title: "FireEye",
    image: "fireeye_color.png",
  },
  {
    title: "Forcepoint",
    image: "forcepoint_color.png",
  },
];
const carousel2 = [
  {
    title: "FTI Consulting",
    image: "fti_consulting_color.png",
  },
  {
    title: "Inspiroz",
    image: "inspiroz_color.png",
  },
  {
    title: "Ivionics",
    image: "ivionics_color.png",
  },
  {
    title: "Kivu Consulting",
    image: "kivu_consulting_color.png",
  },
  {
    title: "Lithia Motors",
    image: "lithia_motors_color.png",
  },
  {
    title: "Lynx Technology Partners",
    image: "lynx_technology_partners_color.png",
  },
  {
    title: "MUFG Mitsubishi Financial Group",
    image: "mufg_mitsubishi_financial_group_color.png",
  },
  {
    title: "NYC Department of Housing Preservation & Development",
    image: "nyc_department_of_housing_preservation_&_development_color.png",
  },
];
const carousel3 = [
  {
    title: "Pentera",
    image: "pentera_color.png",
  },
  {
    title: "Personal Computer Power Center",
    image: "personal_computer_power_center_color.png",
  },
  {
    title: "PwC",
    image: "pwc_color.svg",
  },
  {
    title: "R Street Institute",
    image: "r_street_institute_color.png",
  },
  {
    title: "RampUp Technology",
    image: "rampup_technology_color.png",
  },
  {
    title: "Research Foundation of the City University of New York",
    image: "research_foundation_of_the_city_university_of_new_york_color.png",
  },
  {
    title: "TAM C",
    image: "tam_c_color.png",
  },
  {
    title: "Turner Broadcasting System Inc",
    image: "turner_broadcasting_system_inc_color.png",
  },
];

export const Section3 = function () {
  let carousel_options = {
    type: "loop",
    drag: false,
    focus: "center",
    autoScroll: { autoStart: true, speed: 1, pauseOnHover: false, pauseOnFocus: false, rewind: false, keyboard: false },
    arrows: false,
    resetProgress: false,
    pagination: false,
    mediaQuery: "min",
    clones: 6,
    intersection: { inView: { autoplay: true }, outView: { autoplay: false } },
    breakpoints: {
      "320": { perPage: 2, fixedWidth: 150, gap: 50 },
      "768": { fixedWidth: 164, gap: 60, perPage: 3 },
      "992": { perPage: 4, gap: 70 },
    },
  };

  const refCarousel1 = useRef(null);
  const refCarousel2 = useRef(null);
  const refCarousel3 = useRef(null);

  useEffect(() => {
    const carousel1 = (refCarousel1.current as any).splide;
    const carousel2 = (refCarousel2.current as any).splide;
    const carousel3 = (refCarousel3.current as any).splide;
    if (carousel1) {
      carousel1.on("intersection:in", () => {
        carousel1.Components.AutoScroll.play();
      });
      carousel1.on("intersection:out", () => {
        carousel1.Components.AutoScroll.pause();
      });
    }
    if (carousel2) {
      carousel2.on("intersection:in", () => {
        carousel2.Components.AutoScroll.play();
      });
      carousel2.on("intersection:out", () => {
        carousel2.Components.AutoScroll.pause();
      });
    }
    if (carousel3) {
      carousel3.on("intersection:in", () => {
        carousel3.Components.AutoScroll.play();
      });
      carousel3.on("intersection:out", () => {
        carousel3.Components.AutoScroll.pause();
      });
    }
    return () => {
      carousel1.off("intersection:in");
      carousel1.off("intersection:out");
      carousel2.off("intersection:in");
      carousel2.off("intersection:out");
      carousel3.off("intersection:in");
      carousel3.off("intersection:out");
    };
  }, []);

  return (
    <section className={`${styles.container} cc--component-container`}>
      <h2 className={styles.headline}>Our Boot Camp Learners Are in Demand</h2>
      <p className={styles.content}>
        Our employer-competitive learners have received job offers from thousands of global organizations. As you take
        the next steps in your career, you’ll have access to job referrals from{" "}
        <strong>our network of 250+ partners.</strong>
      </p>
      <div className={styles.bc_logo_carousel}>
        <div className={styles.splide__container}>
          <Splide
            ref={refCarousel1}
            className={styles.splide_carousel}
            options={carousel_options}
            hasTrack={false}
            extensions={{ AutoScroll, Intersection }}
          >
            <SplideTrack>
              {carousel1.map(({ title, image }, index) => (
                <SplideSlide key={title}>
                  <img
                    decoding="async"
                    loading="lazy"
                    className="img-item"
                    src={`https://usa.bootcampcdn.com/wp-content/themes/CodingBootcamp/media/svg-icons/employer-logos/${image}`}
                    width="184"
                    height="86"
                    alt={title}
                  />
                </SplideSlide>
              ))}
            </SplideTrack>
          </Splide>
          <Splide
            ref={refCarousel2}
            className={styles.splide_carousel}
            options={carousel_options}
            hasTrack={false}
            extensions={{ AutoScroll, Intersection }}
          >
            <SplideTrack>
              {carousel2.map(({ title, image }, index) => (
                <SplideSlide key={title}>
                  <img
                    decoding="async"
                    loading="lazy"
                    className="img-item"
                    src={`https://usa.bootcampcdn.com/wp-content/themes/CodingBootcamp/media/svg-icons/employer-logos/${image}`}
                    width="184"
                    height="86"
                    alt={title}
                  />
                </SplideSlide>
              ))}
            </SplideTrack>
          </Splide>
          <Splide
            ref={refCarousel3}
            className={styles.splide_carousel}
            options={carousel_options}
            hasTrack={false}
            extensions={{ AutoScroll, Intersection }}
          >
            <SplideTrack>
              {carousel3.map(({ title, image }, index) => (
                <SplideSlide key={title}>
                  <img
                    decoding="async"
                    loading="lazy"
                    className="img-item"
                    src={`https://usa.bootcampcdn.com/wp-content/themes/CodingBootcamp/media/svg-icons/employer-logos/${image}`}
                    width="184"
                    height="86"
                    alt={title}
                  />
                </SplideSlide>
              ))}
            </SplideTrack>
          </Splide>
        </div>
      </div>
    </section>
  );
};
